import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm, crmFileUrl } from './crmApi';
import type { CrmActivity, CrmAuth } from './crmApi';
import LeadPanel from './LeadPanel';
import { Empty, Head, Spinner, fmtWhen, inputCls, useCrmMe } from './ui';

// Brochure calls with their recordings, newest first — for the Sales Admin to
// listen to. A salesperson sees only their own.

const Player: React.FC<{ auth: CrmAuth; a: CrmActivity }> = ({ auth, a }) => {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const play = async () => {
    setBusy(true);
    try { setUrl(crmFileUrl((await crm.fileLink(auth, a.id, 'recording')).url)); }
    catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return url
    ? <audio controls autoPlay src={url} className="w-full max-w-xs h-9" />
    : <button onClick={play} disabled={busy} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-60 whitespace-nowrap">▶ Play</button>;
};

const CrmRecordings: React.FC<{ auth: CrmAuth; isManager: boolean; embedded?: boolean }> = ({ auth, isManager, embedded }) => {
  const { me } = useCrmMe(auth);
  const [person, setPerson] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ recordings: CrmActivity[]; total: number; page: number; pages: number } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setData(await crm.recordings(auth, { salesPersonId: person || undefined, page })); }
    catch (e: any) { toast.error(e.message); }
  }, [auth, person, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="Recordings" sub={data ? `${data.total} brochure call${data.total === 1 ? '' : 's'} with a recording` : 'Brochure calls with their recordings'} />
      {isManager && (
        <select value={person} onChange={e => { setPerson(e.target.value); setPage(1); }} className={`${inputCls} max-w-[240px]`} aria-label="Salesperson">
          <option value="">Everyone</option>
          {(me?.people || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
      {!data ? <Spinner /> : data.recordings.length === 0 ? (
        <Empty icon="🎧" title="No recordings yet" sub="Every brochure call's recording shows up here." />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {data.recordings.map(a => (
            <div key={a.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex-1 min-w-0">
                <button onClick={() => setOpen(a.leadId)} className="text-sm font-bold text-gray-800 hover:text-purple-700 text-left">#{a.leadNo} {a.leadName || 'No name'}</button>
                <p className="text-xs text-gray-500">{a.byName} · {a.result ? `${a.outcome} → ${a.result}` : a.outcome} · {fmtWhen(a.at)}</p>
                {a.recording && <p className="text-[11px] text-gray-400 truncate">{a.recording.fileName} · {(a.recording.size / 1048576).toFixed(1)} MB</p>}
              </div>
              <Player auth={auth} a={a} />
            </div>
          ))}
        </div>
      )}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-lg border text-xs font-bold disabled:opacity-40">← Newer</button>
          <span className="text-xs text-gray-500">Page {data.page} of {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-lg border text-xs font-bold disabled:opacity-40">Older →</button>
        </div>
      )}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

export default CrmRecordings;
